import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTrip } from '../../contexts/TripContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Plus, MapPin, Loader2, Compass, GripVertical, Share2, Download } from 'lucide-react';
import { format, parseISO, eachDayOfInterval, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { PageHeader } from '../../components/common/PageHeader';
import { jsPDF } from 'jspdf';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

// Drag and drop imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

// Import refactored components
import { CATEGORIES, POI_TYPES } from './constants';
import { ActivityCard } from './ActivityCard';
import { DayNavigation } from './DayNavigation';
import { ActivityDetailsModal } from './ActivityDetailsModal';
import { ActivityViewModal } from './ActivityViewModal';
import { NearbySearchDialog } from './NearbySearchDialog';
import { DuplicateDialog, DeleteConfirmDialog } from './OtherModals';
import { 
  MembersAlertDialog, 
  MembersSelectionDialog, 
  ActivityMembersDialog,
  MembersPaymentAlert
} from './MembersModals';
import { 
  PaymentsAlertDialog, 
  ExistingPaymentsAlert, 
  MarkAsFreeAlert,
  PaymentSplitDialog,
  ActivityPaymentsDialog 
} from './PaymentsModals';

export default function TripItinerary() {
  const { tripId } = useParams();
  const context = useOutletContext();
  const { trip, members } = context || {};
  const { api, user } = useAuth();
  const { t, language } = useLanguage();
  const { itinerary, fetchItinerary, createItineraryItem, updateItineraryItem, deleteItineraryItem, createPayment, fetchPayments, loading, currentTrip } = useTrip();

  // Current day index in carousel
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  
  // Dialog states
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [showNearbyDialog, setShowNearbyDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPaymentSplitDialog, setShowPaymentSplitDialog] = useState(false);
  const [showActivityMembersDialog, setShowActivityMembersDialog] = useState(false);
  const [showActivityPaymentsDialog, setShowActivityPaymentsDialog] = useState(false);
  const [showActivityViewModal, setShowActivityViewModal] = useState(false);
  
  // Item states
  const [viewingActivity, setViewingActivity] = useState(null);
  const [viewingActivityForModal, setViewingActivityForModal] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [duplicatingItem, setDuplicatingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [deletingItemPayments, setDeletingItemPayments] = useState([]);
  const [selectedDuplicateDay, setSelectedDuplicateDay] = useState(null);
  
  // Mobile flow state machine
  const [mobileFlowState, setMobileFlowState] = useState('idle');
  const mobileFlowStateRef = useRef('idle');
  const [savedActivityData, setSavedActivityData] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectAllMembers, setSelectAllMembers] = useState(true);
  
  const updateMobileFlowState = useCallback((newState) => {
    mobileFlowStateRef.current = newState;
    setMobileFlowState(newState);
  }, []);
  
  // Payment states
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [isActivityFree, setIsActivityFree] = useState(false);
  const [existingPayments, setExistingPayments] = useState([]);
  const [showExistingPaymentAlert, setShowExistingPaymentAlert] = useState(false);
  const [showMarkAsFreeAlert, setShowMarkAsFreeAlert] = useState(false);
  const [isRefreshingPayment, setIsRefreshingPayment] = useState(false);
  const [hasExistingQuotas, setHasExistingQuotas] = useState(false);
  const [showMembersPaymentAlert, setShowMembersPaymentAlert] = useState(false);
  const [membersPaymentsToDelete, setMembersPaymentsToDelete] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  
  // PDF export state
  const [showPdfOptionsDialog, setShowPdfOptionsDialog] = useState(false);
  const [pendingPdfData, setPendingPdfData] = useState(null);
  
  // Desktop modal tab
  const [desktopModalTab, setDesktopModalTab] = useState('details');
  const [isDesktop, setIsDesktop] = useState(false);
  
  // Detect screen size
  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 640);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);
  
  // Drag state
  const [activeId, setActiveId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    location: '',
    location_place: null,
    category: 'activity',
    cost: 0,
    is_free: true,
    notes: '',
    purchase_links: [],
    tickets_info: '',
  });

  // Payment split state
  const [paymentSplitData, setPaymentSplitData] = useState([]);

  // Nearby search state
  const [nearbyReferenceType, setNearbyReferenceType] = useState('destination');
  const [nearbyCustomPlace, setNearbyCustomPlace] = useState(null);
  const [nearbyCategory, setNearbyCategory] = useState('restaurant');
  const [nearbyRadius, setNearbyRadius] = useState(3000);
  const [nearbyResults, setNearbyResults] = useState([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);

  // Error state
  const [errorMessage, setErrorMessage] = useState(null);

  // Get currency from trip
  const tripCurrency = currentTrip?.currency || 'BRL';
  
  // Currency formatter
  const { formatCurrency, currencySymbol } = useMemo(() => {
    const config = {
      BRL: { symbol: 'R$', locale: 'pt-BR' },
      USD: { symbol: '$', locale: 'en-US' },
    };
    const curr = config[tripCurrency] || config.BRL;
    
    const format = (value) => {
      if (!value && value !== 0) return '';
      return new Intl.NumberFormat(curr.locale, {
        style: 'currency',
        currency: tripCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    };
    
    return { formatCurrency: format, currencySymbol: curr.symbol };
  }, [tripCurrency]);

  // Helper to format payment description with activity details
  const formatPaymentDescription = (activity, activityDate) => {
    const activityTitle = activity?.title || 'Atividade';
    const date = activityDate ? (typeof activityDate === 'string' ? parseISO(activityDate) : activityDate) : null;
    const dateStr = date ? format(date, "dd/MM/yyyy (EEEE)", { locale: ptBR }) : '';
    const startTime = activity?.start_time || '';
    const endTime = activity?.end_time || '';
    const timeStr = startTime ? (endTime ? `${startTime} - ${endTime}` : `às ${startTime}`) : '';
    const location = activity?.location || '';
    
    let description = `Atividade: "${activityTitle}"`;
    if (dateStr) description += `\nData: ${dateStr}`;
    if (timeStr) description += `\nHorário: ${timeStr}`;
    if (location) description += `\nLocal: ${location}`;
    
    return description;
  };

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Generate array of days from trip dates
  const tripDays = useMemo(() => {
    if (!trip?.start_date || !trip?.end_date) return [];
    const start = typeof trip.start_date === 'string' ? parseISO(trip.start_date) : trip.start_date;
    const end = typeof trip.end_date === 'string' ? parseISO(trip.end_date) : trip.end_date;
    if (start > end) return [];
    return eachDayOfInterval({ start, end });
  }, [trip?.start_date, trip?.end_date]);

  // Current day
  const currentDay = tripDays[currentDayIndex];
  const currentDayNumber = currentDayIndex + 1;
  const totalDays = tripDays.length;

  // Get items for current day
  const currentDayItems = useMemo(() => {
    if (!currentDay || !itinerary) return [];
    return itinerary
      .filter(item => {
        const itemDate = typeof item.date === 'string' ? parseISO(item.date) : item.date;
        return isSameDay(itemDate, currentDay);
      })
      .sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined && a.order !== b.order) {
          return a.order - b.order;
        }
        if (!a.start_time && !b.start_time) return 0;
        if (!a.start_time) return 1;
        if (!b.start_time) return -1;
        return a.start_time.localeCompare(b.start_time);
      });
  }, [currentDay, itinerary]);

  const itemIds = useMemo(() => currentDayItems.map(item => item.id), [currentDayItems]);
  const activeItem = useMemo(() => activeId ? currentDayItems.find(item => item.id === activeId) : null, [activeId, currentDayItems]);

  // Location helpers
  const lastActivityLocation = useMemo(() => {
    if (currentDayItems.length === 0) return null;
    const lastItem = currentDayItems[currentDayItems.length - 1];
    if (lastItem.location_coords?.lat && lastItem.location_coords?.lng) {
      return { name: lastItem.location, lat: lastItem.location_coords.lat, lng: lastItem.location_coords.lng };
    }
    return null;
  }, [currentDayItems]);

  const destinationLocation = useMemo(() => {
    if (trip?.destination_data?.lat && trip?.destination_data?.lng) {
      return { name: trip.destination, lat: trip.destination_data.lat, lng: trip.destination_data.lng };
    }
    return null;
  }, [trip]);

  // Fetch itinerary on mount
  useEffect(() => {
    if (tripId) {
      fetchItinerary(tripId).catch((error) => {
        if (error.response?.status === 400) {
          setErrorMessage(error.response?.data?.detail || 'Esta viagem ainda não tem datas definidas.');
        }
      });
    }
  }, [tripId, fetchItinerary]);

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '', description: '', start_time: '', end_time: '', location: '',
      location_place: null, category: 'activity', cost: 0, is_free: true, notes: '',
      purchase_links: [], tickets_info: '',
    });
    setEditingItem(null);
    setSavedActivityData(null);
    setSelectedMembers([]);
    setSelectAllMembers(true);
    setDesktopModalTab('details');
    updateMobileFlowState('idle');
  };

  // Open activity dialog
  const openActivityDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title || '',
        description: item.description || '',
        start_time: item.start_time || '',
        end_time: item.end_time || '',
        location: item.location || '',
        location_place: item.location_coords ? {
          name: item.location,
          formatted_address: item.address,
          lat: item.location_coords.lat,
          lng: item.location_coords.lng,
          place_id: item.location_coords.place_id
        } : null,
        category: item.category || 'activity',
        cost: item.cost || 0,
        is_free: item.is_free ?? true,
        notes: item.notes || '',
        purchase_links: item.purchase_links || [],
        tickets_info: item.tickets_info || '',
      });
      setSelectedMembers(item.assigned_members || []);
      setSelectAllMembers(item.is_for_everyone ?? true);
    } else {
      resetForm();
    }
    setShowActivityDialog(true);
  };

  // Save activity
  const handleSaveActivity = async () => {
    if (!formData.title.trim()) {
      toast.error('Digite um título para a atividade');
      return;
    }

    try {
      const isForEveryone = isDesktop ? selectAllMembers : true;
      const assignedMembers = isDesktop ? (selectAllMembers ? [] : selectedMembers) : [];
      
      const data = {
        title: formData.title,
        description: formData.description || null,
        date: currentDay.toISOString(),
        start_time: formData.start_time || null,
        end_time: formData.end_time || null,
        location: formData.location_place?.name || formData.location || null,
        address: formData.location_place?.formatted_address || null,
        location_coords: formData.location_place ? {
          lat: formData.location_place.lat,
          lng: formData.location_place.lng,
          place_id: formData.location_place.place_id
        } : null,
        category: formData.category,
        cost: formData.is_free ? 0 : parseFloat(formData.cost) || 0,
        is_free: formData.is_free,
        notes: formData.notes || null,
        purchase_links: formData.purchase_links?.filter(link => link.trim()) || [],
        tickets_info: formData.tickets_info || null,
        is_for_everyone: isForEveryone,
        assigned_members: assignedMembers,
        order: editingItem?.order ?? currentDayItems.length,
      };

      let savedItem = null;
      if (editingItem) {
        savedItem = await updateItineraryItem(tripId, editingItem.id, data);
        toast.success('Atividade atualizada!');
      } else {
        savedItem = await createItineraryItem(tripId, data);
        toast.success('Atividade adicionada!');
      }

      if (!isDesktop && !editingItem) {
        const activityDataForFlow = { ...data, id: savedItem?.id || null };
        setSavedActivityData(activityDataForFlow);
        setShowActivityDialog(false);
        fetchItinerary(tripId);
        updateMobileFlowState('ask_members');
      } else {
        setShowActivityDialog(false);
        resetForm();
        fetchItinerary(tripId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar atividade');
    }
  };

  // Mobile flow handlers
  const handleAddMembersNow = () => {
    setSelectAllMembers(true);
    setSelectedMembers(members?.map(m => m.user_id) || []);
    updateMobileFlowState('select_members');
  };

  const handleAddMembersLater = () => {
    updateMobileFlowState('idle');
    resetForm();
  };

  const handleSaveMembersSelection = async () => {
    if (!savedActivityData) return;
    try {
      const isForEveryone = selectAllMembers;
      const assignedMembers = selectAllMembers ? [] : selectedMembers;
      await updateItineraryItem(tripId, savedActivityData.id, { is_for_everyone: isForEveryone, assigned_members: assignedMembers });
      toast.success('Membros adicionados!');
      fetchItinerary(tripId);
      if (!savedActivityData.is_free && savedActivityData.cost > 0) {
        updateMobileFlowState('ask_payments');
      } else {
        updateMobileFlowState('idle');
        resetForm();
      }
    } catch (error) {
      toast.error('Erro ao salvar membros');
    }
  };

  const handleGeneratePaymentsNow = () => {
    const participantIds = selectAllMembers ? members?.map(m => m.user_id) || [] : selectedMembers;
    if (participantIds.length > 0) {
      const splitData = participantIds.map(userId => {
        const member = members?.find(m => m.user_id === userId);
        return { user_id: userId, user_name: member?.user_name || 'Desconhecido', quotas: 0 };
      });
      setPaymentSplitData(splitData);
      updateMobileFlowState('select_payments');
    }
  };

  const handleGeneratePaymentsLater = () => {
    updateMobileFlowState('idle');
    resetForm();
  };

  // Member selection handlers
  const toggleMemberSelection = (memberId) => {
    setSelectedMembers(prev => {
      const newSelection = prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId];
      setSelectAllMembers(newSelection.length === members?.length);
      return newSelection;
    });
  };

  const handleToggleAllMembers = (checked) => {
    setSelectAllMembers(checked);
    if (checked) {
      setSelectedMembers(members?.map(m => m.user_id) || []);
    } else {
      setSelectedMembers([]);
    }
  };

  // Delete activity
  const openDeleteDialog = async (item) => {
    setDeletingItem(item);
    try {
      const response = await api.get(`/trips/${tripId}/payments`);
      const allPayments = response.data || [];
      const linkedPayments = allPayments.filter(p => p.itinerary_item_id === item.id);
      setDeletingItemPayments(linkedPayments);
    } catch (error) {
      console.error('Error checking payments:', error);
      setDeletingItemPayments([]);
    }
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingItem) return;
    try {
      if (deletingItemPayments.length > 0) {
        for (const payment of deletingItemPayments) {
          await api.delete(`/trips/${tripId}/payments/${payment.id}`);
        }
      }
      await deleteItineraryItem(tripId, deletingItem.id);
      if (deletingItemPayments.length > 0) {
        toast.success(`Atividade e ${deletingItemPayments.length} pagamento(s) excluídos`);
      } else {
        toast.success('Atividade excluída');
      }
      fetchItinerary(tripId);
      fetchPayments(tripId);
    } catch (error) {
      toast.error('Erro ao excluir atividade');
    } finally {
      setShowDeleteDialog(false);
      setDeletingItem(null);
      setDeletingItemPayments([]);
    }
  };

  // Duplicate activity
  const openDuplicateDialog = (item) => {
    setDuplicatingItem(item);
    setSelectedDuplicateDay(null);
    setShowDuplicateDialog(true);
  };

  const handleDuplicateActivity = async () => {
    if (!duplicatingItem || selectedDuplicateDay === null) {
      toast.error('Selecione um dia para duplicar');
      return;
    }
    const targetDay = tripDays[selectedDuplicateDay];
    if (!targetDay) return;
    try {
      const data = {
        title: duplicatingItem.title,
        description: duplicatingItem.description || null,
        date: targetDay.toISOString(),
        start_time: duplicatingItem.start_time || null,
        end_time: duplicatingItem.end_time || null,
        location: duplicatingItem.location || null,
        address: duplicatingItem.address || null,
        location_coords: duplicatingItem.location_coords || null,
        category: duplicatingItem.category || 'activity',
        cost: duplicatingItem.cost || 0,
        is_free: duplicatingItem.is_free ?? true,
        notes: duplicatingItem.notes || null,
        is_for_everyone: duplicatingItem.is_for_everyone ?? true,
        assigned_members: duplicatingItem.assigned_members || [],
      };
      await createItineraryItem(tripId, data);
      toast.success(`Atividade duplicada para o Dia ${selectedDuplicateDay + 1}!`);
      setShowDuplicateDialog(false);
      setDuplicatingItem(null);
      fetchItinerary(tripId);
    } catch (error) {
      toast.error('Erro ao duplicar atividade');
    }
  };

  // Activity members dialog
  const openActivityMembersDialog = (item) => {
    setViewingActivity(item);
    setSelectAllMembers(item.is_for_everyone ?? true);
    setSelectedMembers(item.assigned_members || []);
    setShowActivityMembersDialog(true);
  };

  const handleSaveActivityMembers = async () => {
    if (!viewingActivity) return;
    
    // Check if there are existing payments for this activity
    try {
      const response = await api.get(`/trips/${tripId}/payments`);
      const allPayments = response.data || [];
      const linkedPayments = allPayments.filter(p => p.itinerary_item_id === viewingActivity.id);
      
      if (linkedPayments.length > 0) {
        // There are payments - show alert first
        setMembersPaymentsToDelete(linkedPayments);
        setShowActivityMembersDialog(false);
        setShowMembersPaymentAlert(true);
        return;
      }
    } catch (error) {
      console.error('Error checking payments:', error);
    }
    
    // No payments - save directly
    await saveActivityMembersAndContinue();
  };

  const saveActivityMembersAndContinue = async (openPaymentModal = false) => {
    if (!viewingActivity) return;
    try {
      const isForEveryone = selectAllMembers;
      const assignedMembers = selectAllMembers ? [] : selectedMembers;
      await updateItineraryItem(tripId, viewingActivity.id, { is_for_everyone: isForEveryone, assigned_members: assignedMembers });
      toast.success('Membros atualizados!');
      setShowActivityMembersDialog(false);
      await fetchItinerary(tripId);
      
      if (openPaymentModal) {
        // Create updated activity object with new members
        const updatedActivity = {
          ...viewingActivity,
          is_for_everyone: isForEveryone,
          assigned_members: assignedMembers,
        };
        // Open the payment modal with updated data
        await openActivityPaymentsDialog(updatedActivity);
      } else {
        setViewingActivity(null);
      }
    } catch (error) {
      toast.error('Erro ao salvar membros');
    }
  };

  const handleConfirmMembersPaymentDelete = async () => {
    if (!viewingActivity) return;
    
    try {
      // Delete all linked payments
      for (const payment of membersPaymentsToDelete) {
        await api.delete(`/trips/${tripId}/payments/${payment.id}`);
      }
      toast.success(`${membersPaymentsToDelete.length} pagamento(s) removido(s)`);
      
      // Save members and open payment modal
      setShowMembersPaymentAlert(false);
      setMembersPaymentsToDelete([]);
      await fetchPayments(tripId);
      await saveActivityMembersAndContinue(true);
    } catch (error) {
      toast.error('Erro ao processar');
    }
  };

  const handleCancelMembersPaymentAlert = () => {
    setShowMembersPaymentAlert(false);
    setMembersPaymentsToDelete([]);
    setShowActivityMembersDialog(true);
  };

  // Open activity view modal
  const openActivityViewModal = async (item) => {
    setViewingActivityForModal(item);
    try {
      const response = await api.get(`/trips/${tripId}/payments`);
      setAllPayments(response.data || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
      setAllPayments([]);
    }
    setShowActivityViewModal(true);
  };

  // Export activity to PDF
  const exportActivityToPdf = async (item) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      let yPos = 20;
      
      // Helper to add text with word wrap
      const addText = (text, x, y, maxWidth, fontSize = 10, style = 'normal') => {
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', style);
        const lines = doc.splitTextToSize(text, maxWidth);
        doc.text(lines, x, y);
        return y + (lines.length * fontSize * 0.4);
      };

      // Header - Trip name and activity title
      doc.setFillColor(249, 115, 22); // Orange
      doc.rect(0, 0, pageWidth, 35, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.text(currentTrip?.name || 'Viagem', margin, 12);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(item.title || 'Atividade', margin, 26);
      
      yPos = 45;
      doc.setTextColor(51, 51, 51);

      // Category badge
      const categoryLabel = CATEGORIES[item.category]?.label || 'Outro';
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Categoria: ${categoryLabel}`, margin, yPos);
      if (item.is_free) {
        doc.setTextColor(34, 197, 94);
        doc.text(' (Gratuito)', margin + doc.getTextWidth(`Categoria: ${categoryLabel}`), yPos);
        doc.setTextColor(51, 51, 51);
      }
      yPos += 10;

      // Date and time
      if (item.date) {
        const dateStr = format(
          typeof item.date === 'string' ? parseISO(item.date) : item.date,
          "dd 'de' MMMM 'de' yyyy (EEEE)",
          { locale: ptBR }
        );
        yPos = addText(`Data: ${dateStr}`, margin, yPos, pageWidth - margin * 2, 10, 'bold');
        yPos += 2;
      }
      
      if (item.start_time || item.end_time) {
        const timeStr = item.start_time + (item.end_time ? ` até ${item.end_time}` : '');
        yPos = addText(`Horário: ${timeStr}`, margin, yPos, pageWidth - margin * 2);
        yPos += 2;
      }

      // Location
      if (item.location) {
        yPos = addText(`Local: ${item.location}`, margin, yPos, pageWidth - margin * 2, 10, 'bold');
        yPos += 2;
      }
      if (item.address) {
        yPos = addText(`Endereço: ${item.address}`, margin, yPos, pageWidth - margin * 2);
        yPos += 2;
      }

      yPos += 5;

      // Description
      if (item.description) {
        doc.setFillColor(245, 245, 245);
        doc.rect(margin - 5, yPos - 5, pageWidth - margin * 2 + 10, 25, 'F');
        doc.setFont('helvetica', 'bold');
        doc.text('Descrição:', margin, yPos + 2);
        doc.setFont('helvetica', 'normal');
        yPos = addText(item.description, margin, yPos + 8, pageWidth - margin * 2);
        yPos += 10;
      }

      // Notes
      if (item.notes) {
        doc.setFillColor(254, 249, 195); // Yellow
        doc.rect(margin - 5, yPos - 5, pageWidth - margin * 2 + 10, 25, 'F');
        doc.setFont('helvetica', 'bold');
        doc.text('Observações:', margin, yPos + 2);
        doc.setFont('helvetica', 'normal');
        yPos = addText(item.notes, margin, yPos + 8, pageWidth - margin * 2);
        yPos += 10;
      }

      // Cost
      if (!item.is_free && item.cost > 0) {
        doc.setFont('helvetica', 'bold');
        doc.text(`Custo: ${formatCurrency(item.cost)}`, margin, yPos);
        yPos += 8;
      }

      // Links
      if (item.purchase_links && item.purchase_links.length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.text('Links úteis:', margin, yPos);
        yPos += 6;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(59, 130, 246); // Blue
        item.purchase_links.forEach(link => {
          if (link) {
            yPos = addText(link, margin + 5, yPos, pageWidth - margin * 2 - 5, 9);
            yPos += 2;
          }
        });
        doc.setTextColor(51, 51, 51);
        yPos += 5;
      }

      // Tickets info
      if (item.tickets_info) {
        doc.setFillColor(243, 232, 255); // Purple
        doc.rect(margin - 5, yPos - 5, pageWidth - margin * 2 + 10, 25, 'F');
        doc.setFont('helvetica', 'bold');
        doc.text('Ingressos/Reservas:', margin, yPos + 2);
        doc.setFont('helvetica', 'normal');
        yPos = addText(item.tickets_info, margin, yPos + 8, pageWidth - margin * 2);
        yPos += 10;
      }

      // Members
      const participantIds = item.is_for_everyone 
        ? members?.map(m => m.user_id) || []
        : item.assigned_members || [];
      
      if (participantIds.length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.text(`Participantes (${participantIds.length}):`, margin, yPos);
        yPos += 6;
        doc.setFont('helvetica', 'normal');
        
        const participantNames = members
          ?.filter(m => participantIds.includes(m.user_id))
          .map(m => m.user_name)
          .join(', ');
        
        if (participantNames) {
          yPos = addText(participantNames, margin + 5, yPos, pageWidth - margin * 2 - 5);
        }
      }

      // Footer
      const footerY = doc.internal.pageSize.getHeight() - 15;
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Gerado por ViajeJunto em ${format(new Date(), "dd/MM/yyyy 'às' HH:mm")}`, margin, footerY);

      // Generate PDF blob
      const fileName = `${item.title?.replace(/[^a-zA-Z0-9]/g, '_') || 'atividade'}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      const pdfBlob = doc.output('blob');
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

      // Check if mobile device
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      if (isMobile) {
        // Show options dialog on mobile
        setPendingPdfData({ blob: pdfBlob, file: pdfFile, fileName, doc });
        setShowPdfOptionsDialog(true);
      } else {
        // Desktop: direct download
        doc.save(fileName);
        toast.success('PDF exportado com sucesso!');
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erro ao exportar PDF');
    }
  };

  // Handle PDF share
  const handleSharePdf = async () => {
    if (!pendingPdfData) return;
    
    try {
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [pendingPdfData.file] })) {
        await navigator.share({
          files: [pendingPdfData.file],
          title: pendingPdfData.fileName,
        });
        toast.success('PDF compartilhado!');
      } else {
        // Try to share anyway without checking canShare
        try {
          await navigator.share({
            files: [pendingPdfData.file],
            title: pendingPdfData.fileName,
          });
          toast.success('PDF compartilhado!');
        } catch (innerError) {
          // If share fails, fallback to download
          pendingPdfData.doc.save(pendingPdfData.fileName);
          toast.success('PDF salvo na pasta Downloads!');
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // User cancelled, do nothing
      } else {
        // Fallback to download
        pendingPdfData.doc.save(pendingPdfData.fileName);
        toast.success('PDF salvo na pasta Downloads!');
      }
    }
    setShowPdfOptionsDialog(false);
    setPendingPdfData(null);
  };

  // Handle PDF download
  const handleDownloadPdf = () => {
    if (!pendingPdfData) return;
    
    pendingPdfData.doc.save(pendingPdfData.fileName);
    toast.success('PDF salvo na pasta Downloads!');
    setShowPdfOptionsDialog(false);
    setPendingPdfData(null);
  };

  // Activity payments dialog
  const openActivityPaymentsDialog = async (item) => {
    setViewingActivity(item);
    setPaymentAmount(item.cost || 0);
    setIsActivityFree(item.is_free ?? false);
    setIsRefreshingPayment(false);
    setHasExistingQuotas(false);
    
    const participantIds = item.is_for_everyone ? members?.map(m => m.user_id) || [] : item.assigned_members || [];
    if (participantIds.length === 0) {
      toast.error('Adicione membros à atividade primeiro');
      return;
    }
    
    try {
      const response = await api.get(`/trips/${tripId}/payments`);
      const allPayments = response.data || [];
      const linkedPayments = allPayments.filter(p => p.itinerary_item_id === item.id);
      const hasStartedPayments = linkedPayments.some(p => p.member_payments?.some(mp => mp.status === 'paid' || mp.is_paid));
      setExistingPayments(linkedPayments);
      
      // Check for activity in the past
      const eventDate = item.date ? new Date(item.date) : null;
      if (eventDate && eventDate < new Date()) {
        toast.error('Não é possível gerar pagamentos para atividades passadas.');
        return;
      }
      
      // If there are existing payments, pre-fill quotas from member_payments
      if (linkedPayments.length > 0) {
        const totalAmount = linkedPayments.reduce((sum, p) => sum + (p.total_amount || 0), 0);
        setPaymentAmount(totalAmount);
        setHasExistingQuotas(true);
        
        // Build split data from existing member_payments
        const memberPaymentsMap = {};
        linkedPayments.forEach(payment => {
          payment.member_payments?.forEach(mp => {
            if (!memberPaymentsMap[mp.user_id]) {
              memberPaymentsMap[mp.user_id] = { amount: 0, paid: 0, isPaid: false };
            }
            memberPaymentsMap[mp.user_id].amount += mp.amount_due || 0;
            memberPaymentsMap[mp.user_id].paid += mp.amount_paid || 0;
            memberPaymentsMap[mp.user_id].isPaid = mp.is_paid || memberPaymentsMap[mp.user_id].isPaid;
          });
        });
        
        // Calculate expected quota value (equal split)
        const quotaValue = participantIds.length > 0 ? totalAmount / participantIds.length : 0;
        
        // Check if this was a manual distribution (values are not equal)
        const amounts = Object.values(memberPaymentsMap).map(m => m.amount);
        const isManualDistribution = amounts.length > 1 && amounts.some(a => Math.abs(a - quotaValue) > 0.01);
        
        const splitData = participantIds.map(userId => {
          const member = members?.find(m => m.user_id === userId);
          const memberData = memberPaymentsMap[userId] || { amount: 0, paid: 0, isPaid: false };
          
          if (isManualDistribution) {
            // Manual mode: use actual amounts
            return { 
              user_id: userId, 
              user_name: member?.user_name || 'Desconhecido', 
              quotas: 0,
              manualAmount: memberData.amount,
              amount_paid: memberData.paid,
              is_paid: memberData.isPaid
            };
          } else {
            // Quota mode: calculate quotas
            const quotas = quotaValue > 0 ? Math.round(memberData.amount / quotaValue) : 0;
            return { 
              user_id: userId, 
              user_name: member?.user_name || 'Desconhecido', 
              quotas: quotas,
              manualAmount: 0,
              amount_paid: memberData.paid,
              is_paid: memberData.isPaid
            };
          }
        });
        setPaymentSplitData(splitData);
        
        // If any payment has been made, show warning and don't allow editing
        if (hasStartedPayments) {
          toast.info('Este roteiro já possui pagamentos realizados. Visualização apenas.');
        }
        
        setShowActivityPaymentsDialog(true);
        return;
      }
    } catch (error) {
      console.error('Error checking existing payments:', error);
    }
    
    // No existing payments - create fresh split data
    const splitData = participantIds.map(userId => {
      const member = members?.find(m => m.user_id === userId);
      return { user_id: userId, user_name: member?.user_name || 'Desconhecido', quotas: 0, manualAmount: 0 };
    });
    setPaymentSplitData(splitData);
    setShowActivityPaymentsDialog(true);
  };

  const handleRecreatePayments = async () => {
    setShowExistingPaymentAlert(false);
    try {
      for (const payment of existingPayments) {
        await api.delete(`/trips/${tripId}/payments/${payment.id}`);
      }
      toast.success('Pagamentos anteriores removidos');
    } catch (error) {
      toast.error('Erro ao remover pagamentos anteriores');
      return;
    }
    const participantIds = viewingActivity.is_for_everyone ? members?.map(m => m.user_id) || [] : viewingActivity.assigned_members || [];
    const splitData = participantIds.map(userId => {
      const member = members?.find(m => m.user_id === userId);
      return { user_id: userId, user_name: member?.user_name || 'Desconhecido', quotas: 0 };
    });
    setPaymentSplitData(splitData);
    setExistingPayments([]);
    setShowActivityPaymentsDialog(true);
  };

  const handleDeleteExistingPayments = async () => {
    setShowExistingPaymentAlert(false);
    try {
      for (const payment of existingPayments) {
        await api.delete(`/trips/${tripId}/payments/${payment.id}`);
      }
      toast.success('Pagamentos removidos com sucesso');
      setExistingPayments([]);
      setViewingActivity(null);
      await fetchPayments(tripId);
    } catch (error) {
      toast.error('Erro ao remover pagamentos');
    }
  };

  const handleSaveActivityPayments = async () => {
    if (!viewingActivity) return;
    
    // If marked as free, update the activity and remove any existing payments
    if (isActivityFree) {
      try {
        // Delete existing payments
        if (existingPayments.length > 0) {
          for (const payment of existingPayments) {
            await api.delete(`/trips/${tripId}/payments/${payment.id}`);
          }
        }
        // Update activity to be free
        await updateItineraryItem(tripId, viewingActivity.id, { 
          is_free: true, 
          cost: 0 
        });
        toast.success('Atividade marcada como gratuita!');
        setShowActivityPaymentsDialog(false);
        setViewingActivity(null);
        setPaymentSplitData([]);
        setIsActivityFree(false);
        setHasExistingQuotas(false);
        setIsRefreshingPayment(false);
        fetchItinerary(tripId);
        fetchPayments(tripId);
      } catch (error) {
        toast.error('Erro ao salvar atividade');
      }
      return;
    }
    
    // If has existing quotas and not refreshing, just close the dialog (view only mode)
    if (hasExistingQuotas && !isRefreshingPayment) {
      setShowActivityPaymentsDialog(false);
      setViewingActivity(null);
      setPaymentSplitData([]);
      setHasExistingQuotas(false);
      return;
    }
    
    if (paymentAmount <= 0) {
      toast.info('Nenhum valor definido para pagamento');
      setShowActivityPaymentsDialog(false);
      setViewingActivity(null);
      setPaymentSplitData([]);
      setHasExistingQuotas(false);
      setIsRefreshingPayment(false);
      return;
    }
    
    // Check if using manual mode (any participant has manualAmount > 0)
    const isManualMode = paymentSplitData.some(p => p.manualAmount > 0);
    
    if (isManualMode) {
      // Manual mode validation
      const participantsWithAmount = paymentSplitData.filter(p => (p.manualAmount || 0) > 0);
      if (participantsWithAmount.length === 0) {
        toast.error('Distribua o valor entre os participantes');
        return;
      }
      
      const totalManualAssigned = paymentSplitData.reduce((sum, p) => sum + (p.manualAmount || 0), 0);
      const remainingAmount = paymentAmount - totalManualAssigned;
      
      if (Math.abs(remainingAmount) > 0.01) {
        if (remainingAmount > 0) {
          toast.error(`Faltam ${formatCurrency(remainingAmount)} para completar o rateio.`);
        } else {
          toast.error(`Excesso de ${formatCurrency(Math.abs(remainingAmount))} no rateio.`);
        }
        return;
      }
      
      const activityTitle = viewingActivity.title || '';
      const activityId = viewingActivity.id;
      const categoryMap = { 'meal': 'food', 'activity': 'activities', 'transport': 'transport', 'accommodation': 'accommodation', 'shopping': 'shopping' };
      const expenseCategory = categoryMap[viewingActivity.category] || 'other';
      
      try {
        // Delete existing payments if any
        if (existingPayments.length > 0) {
          for (const payment of existingPayments) {
            await api.delete(`/trips/${tripId}/payments/${payment.id}`);
          }
        }
        
        // Update activity cost if changed
        if (paymentAmount !== viewingActivity.cost) {
          await updateItineraryItem(tripId, viewingActivity.id, { 
            cost: paymentAmount, 
            is_free: false 
          });
        }
        
        // Create individual payments for each participant with manual amounts
        const selectedMemberIds = participantsWithAmount.map(p => p.user_id);
        const paymentData = {
          title: `Rateio: ${activityTitle}`,
          description: formatPaymentDescription(viewingActivity, viewingActivity.date),
          total_amount: totalManualAssigned,
          category: expenseCategory,
          payment_type: 'selective',
          due_date: viewingActivity.date || null,
          selected_member_ids: selectedMemberIds,
          itinerary_item_id: activityId,
          manual_amounts: participantsWithAmount.map(p => ({
            user_id: p.user_id,
            amount: p.manualAmount
          })),
        };
        await createPayment(tripId, paymentData);
        toast.success(`Pagamento ${hasExistingQuotas ? 'atualizado' : 'gerado'} com sucesso! Valor: ${formatCurrency(totalManualAssigned)}`);
        setShowActivityPaymentsDialog(false);
        setViewingActivity(null);
        setPaymentSplitData([]);
        setExistingPayments([]);
        setHasExistingQuotas(false);
        setIsRefreshingPayment(false);
        await fetchPayments(tripId);
        fetchItinerary(tripId);
      } catch (error) {
        console.error('Error creating payment:', error);
        toast.error(error.response?.data?.detail || 'Erro ao gerar pagamento');
      }
      return;
    }
    
    // Quota mode (existing logic)
    const participantsWithQuotas = paymentSplitData.filter(p => p.quotas > 0);
    if (participantsWithQuotas.length === 0) {
      toast.error('Distribua pelo menos uma cota');
      return;
    }
    
    // Validate that total quotas matches total participants
    const totalParticipants = paymentSplitData.length;
    const totalQuotas = paymentSplitData.reduce((sum, p) => sum + p.quotas, 0);
    const quotaValue = totalParticipants > 0 ? paymentAmount / totalParticipants : 0;
    const totalAmountFromQuotas = totalQuotas * quotaValue;
    
    // Check if the sum of quotas matches the expected amount (allow small rounding errors)
    if (Math.abs(totalAmountFromQuotas - paymentAmount) > 0.01 && totalQuotas !== totalParticipants) {
      const diff = paymentAmount - totalAmountFromQuotas;
      const diffFormatted = formatCurrency(Math.abs(diff));
      if (diff > 0) {
        toast.error(`Faltam ${diffFormatted} para completar o rateio. Distribua mais ${Math.ceil(diff / quotaValue)} cota(s).`);
      } else {
        toast.error(`Excesso de ${diffFormatted} no rateio. Remova ${Math.ceil(Math.abs(diff) / quotaValue)} cota(s).`);
      }
      return;
    }
    
    const activityTitle = viewingActivity.title || '';
    const activityId = viewingActivity.id;
    const categoryMap = { 'meal': 'food', 'activity': 'activities', 'transport': 'transport', 'accommodation': 'accommodation', 'shopping': 'shopping' };
    const expenseCategory = categoryMap[viewingActivity.category] || 'other';
    
    try {
      // First, delete existing payments if any
      if (existingPayments.length > 0) {
        for (const payment of existingPayments) {
          await api.delete(`/trips/${tripId}/payments/${payment.id}`);
        }
      }
      
      // Update activity cost if changed
      if (paymentAmount !== viewingActivity.cost) {
        await updateItineraryItem(tripId, viewingActivity.id, { 
          cost: paymentAmount, 
          is_free: false 
        });
      }
      
      const totalAmount = totalQuotas * quotaValue;
      const selectedMemberIds = participantsWithQuotas.map(p => p.user_id);
      const paymentData = {
        title: `Rateio: ${activityTitle}`,
        description: formatPaymentDescription(viewingActivity, viewingActivity.date),
        total_amount: totalAmount,
        category: expenseCategory,
        payment_type: 'selective',
        due_date: viewingActivity.date || null,
        selected_member_ids: selectedMemberIds,
        itinerary_item_id: activityId,
      };
      await createPayment(tripId, paymentData);
      toast.success(`Pagamento ${hasExistingQuotas ? 'atualizado' : 'gerado'} com sucesso! Valor: ${formatCurrency(totalAmount)}`);
      setShowActivityPaymentsDialog(false);
      setViewingActivity(null);
      setPaymentSplitData([]);
      setExistingPayments([]);
      setHasExistingQuotas(false);
      setIsRefreshingPayment(false);
      await fetchPayments(tripId);
      fetchItinerary(tripId);
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error(error.response?.data?.detail || 'Erro ao gerar pagamento');
    }
  };

  // Handler for showing mark as free alert
  const handleShowMarkAsFreeAlert = () => {
    setShowActivityPaymentsDialog(false);
    setShowMarkAsFreeAlert(true);
  };

  // Handler for confirming mark as free (deletes payments)
  const handleConfirmMarkAsFree = async () => {
    setShowMarkAsFreeAlert(false);
    if (!viewingActivity) return;
    
    try {
      // Delete existing payments
      if (existingPayments.length > 0) {
        for (const payment of existingPayments) {
          await api.delete(`/trips/${tripId}/payments/${payment.id}`);
        }
      }
      
      // Update activity to be free
      await updateItineraryItem(tripId, viewingActivity.id, { 
        is_free: true, 
        cost: 0 
      });
      
      toast.success(`${existingPayments.length} pagamento(s) excluído(s). Atividade marcada como gratuita!`);
      setExistingPayments([]);
      setViewingActivity(null);
      setPaymentSplitData([]);
      setIsActivityFree(false);
      await fetchPayments(tripId);
      fetchItinerary(tripId);
    } catch (error) {
      toast.error('Erro ao processar');
    }
  };

  // Cancel mark as free - return to payment dialog
  const handleCancelMarkAsFree = () => {
    setShowMarkAsFreeAlert(false);
    setShowActivityPaymentsDialog(true);
  };

  // Payment split save (mobile flow)
  const handleSavePaymentSplit = async () => {
    const participantsWithQuotas = paymentSplitData.filter(p => p.quotas > 0);
    if (participantsWithQuotas.length === 0) {
      toast.error('Distribua pelo menos uma cota');
      return;
    }
    const activityCost = savedActivityData?.cost || formData.cost || 0;
    const activityTitle = savedActivityData?.title || formData.title || '';
    const activityId = savedActivityData?.id || null;
    const totalParticipants = paymentSplitData.length;
    const quotaValue = totalParticipants > 0 ? activityCost / totalParticipants : 0;
    const categoryMap = { 'meal': 'food', 'activity': 'activities', 'transport': 'transport', 'accommodation': 'accommodation', 'shopping': 'shopping' };
    const activityCategory = savedActivityData?.category || formData.category || 'activity';
    const expenseCategory = categoryMap[activityCategory] || 'other';
    try {
      const totalQuotas = participantsWithQuotas.reduce((sum, p) => sum + p.quotas, 0);
      const totalAmount = totalQuotas * quotaValue;
      const selectedMemberIds = participantsWithQuotas.map(p => p.user_id);
      
      // Build activity object for description
      const activityForDescription = savedActivityData || {
        title: activityTitle,
        start_time: formData.start_time,
        end_time: formData.end_time,
        location: formData.location,
      };
      
      const paymentData = {
        title: `Rateio: ${activityTitle}`,
        description: formatPaymentDescription(activityForDescription, currentDay),
        total_amount: totalAmount,
        category: expenseCategory,
        payment_type: 'selective',
        due_date: currentDay?.toISOString() || null,
        selected_member_ids: selectedMemberIds,
        itinerary_item_id: activityId,
      };
      await createPayment(tripId, paymentData);
      toast.success(`Pagamento gerado com sucesso! Valor: ${formatCurrency(totalAmount)}`);
      setShowPaymentSplitDialog(false);
      updateMobileFlowState('idle');
      setPaymentSplitData([]);
      resetForm();
      await fetchPayments(tripId);
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error(error.response?.data?.detail || 'Erro ao gerar pagamento');
    }
  };

  // Drag handlers
  const handleDragStart = (event) => setActiveId(event.active.id);
  
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const oldIndex = currentDayItems.findIndex(item => item.id === active.id);
    const newIndex = currentDayItems.findIndex(item => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newItems = arrayMove(currentDayItems, oldIndex, newIndex);
    try {
      const updates = newItems.map((item, index) => updateItineraryItem(tripId, item.id, { order: index }));
      await Promise.all(updates);
      toast.success('Ordem atualizada!');
      fetchItinerary(tripId);
    } catch (error) {
      toast.error('Erro ao reordenar');
      fetchItinerary(tripId);
    }
  };

  // Nearby search
  const handleSearchNearby = async () => {
    let referenceLocation = null;
    if (nearbyReferenceType === 'destination' && destinationLocation) {
      referenceLocation = destinationLocation;
    } else if (nearbyReferenceType === 'last_activity' && lastActivityLocation) {
      referenceLocation = lastActivityLocation;
    } else if (nearbyReferenceType === 'custom' && nearbyCustomPlace) {
      referenceLocation = nearbyCustomPlace;
    }
    if (!referenceLocation?.lat || !referenceLocation?.lng) {
      toast.error('Selecione um ponto de referência válido');
      return;
    }
    setNearbyLoading(true);
    try {
      const params = new URLSearchParams({ lat: referenceLocation.lat, lng: referenceLocation.lng, radius: nearbyRadius, type: nearbyCategory });
      const response = await api.get(`/places/nearby?${params}`);
      setNearbyResults(response.data || []);
      if (response.data.length === 0) toast.info('Nenhum local encontrado nesta área');
    } catch (error) {
      toast.error('Erro ao buscar locais próximos');
      setNearbyResults([]);
    } finally {
      setNearbyLoading(false);
    }
  };

  const addNearbyAsActivity = (place) => {
    setFormData({
      ...formData,
      title: place.name,
      location: place.name,
      location_place: {
        place_id: place.place_id, name: place.name, formatted_address: place.formatted_address,
        lat: place.lat, lng: place.lng, rating: place.rating
      },
      category: nearbyCategory === 'restaurant' || nearbyCategory === 'cafe' || nearbyCategory === 'bar' ? 'meal' 
        : nearbyCategory === 'lodging' ? 'accommodation' : nearbyCategory === 'shopping_mall' ? 'shopping' : 'activity'
    });
    setShowNearbyDialog(false);
    setShowActivityDialog(true);
  };

  // Navigation
  const goToPreviousDay = () => { if (currentDayIndex > 0) setCurrentDayIndex(currentDayIndex - 1); };
  const goToNextDay = () => { if (currentDayIndex < totalDays - 1) setCurrentDayIndex(currentDayIndex + 1); };
  const goToDay = (index) => setCurrentDayIndex(index);

  // Error state
  if (errorMessage) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <MapPin className="w-16 h-16 text-slate-400 mb-4" />
        <h2 className="text-xl font-semibold text-slate-300 mb-2">Roteiro não disponível</h2>
        <p className="text-slate-500">{errorMessage}</p>
      </div>
    );
  }

  // Loading state
  if (loading && !itinerary) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // No dates defined
  if (tripDays.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <MapPin className="w-16 h-16 text-slate-400 mb-4" />
        <h2 className="text-xl font-semibold text-slate-300 mb-2">Defina as datas da viagem</h2>
        <p className="text-slate-500">Adicione as datas de início e fim para criar o roteiro.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20 overflow-x-hidden">
      <PageHeader title="Roteiro" description={`${totalDays} dias de viagem`} screenKey="itinerary" />

      {/* Day Navigation */}
      <DayNavigation
        tripDays={tripDays}
        currentDayIndex={currentDayIndex}
        currentDay={currentDay}
        itinerary={itinerary}
        onPreviousDay={goToPreviousDay}
        onNextDay={goToNextDay}
        onGoToDay={goToDay}
      />

      {/* Drag hint */}
      {currentDayItems.length > 1 && (
        <p className="text-xs text-slate-500 text-center flex items-center justify-center gap-1">
          <GripVertical className="w-3 h-3" />
          Arraste para reordenar as atividades
        </p>
      )}

      {/* Activities List with DnD */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            <AnimatePresence mode="wait">
              {currentDayItems.length === 0 ? (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="bg-slate-800/50 rounded-xl p-8 border border-dashed border-slate-700 text-center">
                  <MapPin className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 mb-1">Nenhuma atividade neste dia</p>
                  <p className="text-sm text-slate-500">Adicione atividades ao roteiro</p>
                </motion.div>
              ) : (
                currentDayItems.map((item, index) => (
                  <motion.div key={item.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }} transition={{ delay: index * 0.03 }}>
                    <ActivityCard
                      item={item}
                      onEdit={openActivityDialog}
                      onDelete={openDeleteDialog}
                      onDuplicate={openDuplicateDialog}
                      onViewMembers={openActivityMembersDialog}
                      onViewPayments={openActivityPaymentsDialog}
                      onView={openActivityViewModal}
                      onExportPdf={exportActivityToPdf}
                      isDragging={activeId === item.id}
                      formatCurrency={formatCurrency}
                    />
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>
        </SortableContext>

        <DragOverlay>
          {activeItem ? (
            <div className="opacity-90 shadow-2xl">
              <ActivityCard item={activeItem} onEdit={() => {}} onDelete={() => {}} onDuplicate={() => {}}
                onViewMembers={() => {}} onViewPayments={() => {}} onView={() => {}} onExportPdf={() => {}} isDragging={false} formatCurrency={formatCurrency} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <Button onClick={() => openActivityDialog()} className="w-full sm:flex-1 bg-orange-500 hover:bg-orange-600" data-testid="add-activity-btn">
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Atividade
        </Button>
        <Button variant="outline" onClick={() => setShowNearbyDialog(true)}
          className="w-full sm:flex-1 border-slate-600 text-slate-300 hover:bg-slate-700" data-testid="nearby-search-btn">
          <Compass className="w-4 h-4 mr-2" />
          Ver opções ao redor
        </Button>
      </div>

      {/* Activity Details Modal */}
      <ActivityDetailsModal
        open={showActivityDialog}
        onOpenChange={setShowActivityDialog}
        editingItem={editingItem}
        formData={formData}
        setFormData={setFormData}
        currentDay={currentDay}
        isDesktop={isDesktop}
        desktopModalTab={desktopModalTab}
        setDesktopModalTab={setDesktopModalTab}
        selectAllMembers={selectAllMembers}
        selectedMembers={selectedMembers}
        members={members}
        onToggleAllMembers={handleToggleAllMembers}
        onToggleMemberSelection={toggleMemberSelection}
        tripCurrency={tripCurrency}
        formatCurrency={formatCurrency}
        onSave={handleSaveActivity}
      />

      {/* Mobile: Add Members Alert */}
      <MembersAlertDialog
        open={mobileFlowState === 'ask_members'}
        onOpenChange={(open) => { if (!open && mobileFlowStateRef.current === 'ask_members') updateMobileFlowState('idle'); }}
        onAddNow={handleAddMembersNow}
        onAddLater={handleAddMembersLater}
      />

      {/* Mobile: Members Selection Dialog */}
      <MembersSelectionDialog
        open={mobileFlowState === 'select_members'}
        onOpenChange={(open) => { if (!open && mobileFlowStateRef.current === 'select_members') updateMobileFlowState('idle'); }}
        activityTitle={savedActivityData?.title}
        members={members}
        selectedMembers={selectedMembers}
        selectAllMembers={selectAllMembers}
        onToggleAllMembers={handleToggleAllMembers}
        onToggleMemberSelection={toggleMemberSelection}
        onSave={handleSaveMembersSelection}
        onCancel={() => { updateMobileFlowState('idle'); resetForm(); }}
      />

      {/* Mobile: Payment Alert */}
      <PaymentsAlertDialog
        open={mobileFlowState === 'ask_payments'}
        onOpenChange={(open) => { if (!open && mobileFlowStateRef.current === 'ask_payments') updateMobileFlowState('idle'); }}
        onGenerateNow={handleGeneratePaymentsNow}
        onGenerateLater={handleGeneratePaymentsLater}
        activityCost={savedActivityData?.cost}
        formatCurrency={formatCurrency}
      />

      {/* Payment Split Dialog (Mobile Flow) */}
      <PaymentSplitDialog
        open={mobileFlowState === 'select_payments' || showPaymentSplitDialog}
        onOpenChange={(open) => { if (!open) { updateMobileFlowState('idle'); setShowPaymentSplitDialog(false); resetForm(); } }}
        activityTitle={savedActivityData?.title}
        activityCost={savedActivityData?.cost || formData.cost}
        paymentSplitData={paymentSplitData}
        setPaymentSplitData={setPaymentSplitData}
        formatCurrency={formatCurrency}
        onSave={handleSavePaymentSplit}
        onCancel={() => { updateMobileFlowState('idle'); setShowPaymentSplitDialog(false); resetForm(); }}
      />

      {/* Duplicate Dialog */}
      <DuplicateDialog
        open={showDuplicateDialog}
        onOpenChange={setShowDuplicateDialog}
        item={duplicatingItem}
        tripDays={tripDays}
        currentDayIndex={currentDayIndex}
        selectedDuplicateDay={selectedDuplicateDay}
        setSelectedDuplicateDay={setSelectedDuplicateDay}
        onDuplicate={handleDuplicateActivity}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={showDeleteDialog}
        onOpenChange={(open) => { setShowDeleteDialog(open); if (!open) { setDeletingItem(null); setDeletingItemPayments([]); } }}
        item={deletingItem}
        linkedPayments={deletingItemPayments}
        onConfirm={handleConfirmDelete}
        onCancel={() => { setDeletingItem(null); setDeletingItemPayments([]); }}
      />

      {/* Activity Members Dialog */}
      <ActivityMembersDialog
        open={showActivityMembersDialog}
        onOpenChange={setShowActivityMembersDialog}
        activity={viewingActivity}
        members={members}
        selectedMembers={selectedMembers}
        selectAllMembers={selectAllMembers}
        onToggleAllMembers={handleToggleAllMembers}
        onToggleMemberSelection={toggleMemberSelection}
        onSave={handleSaveActivityMembers}
        onCancel={() => setShowActivityMembersDialog(false)}
      />

      {/* Activity View Modal */}
      <ActivityViewModal
        open={showActivityViewModal}
        onOpenChange={setShowActivityViewModal}
        activity={viewingActivityForModal}
        members={members}
        payments={allPayments}
        formatCurrency={formatCurrency}
      />

      {/* Members Payment Alert (when changing members with existing payments) */}
      <MembersPaymentAlert
        open={showMembersPaymentAlert}
        onOpenChange={setShowMembersPaymentAlert}
        existingPayments={membersPaymentsToDelete}
        formatCurrency={formatCurrency}
        onCancel={handleCancelMembersPaymentAlert}
        onConfirm={handleConfirmMembersPaymentDelete}
      />

      {/* Existing Payments Alert */}
      <ExistingPaymentsAlert
        open={showExistingPaymentAlert}
        onOpenChange={setShowExistingPaymentAlert}
        existingPayments={existingPayments}
        formatCurrency={formatCurrency}
        onCancel={() => { setShowExistingPaymentAlert(false); setViewingActivity(null); }}
        onDelete={handleDeleteExistingPayments}
        onRecreate={handleRecreatePayments}
      />

      {/* Activity Payments Dialog */}
      <ActivityPaymentsDialog
        open={showActivityPaymentsDialog}
        onOpenChange={setShowActivityPaymentsDialog}
        activity={viewingActivity}
        paymentAmount={paymentAmount}
        setPaymentAmount={setPaymentAmount}
        isFree={isActivityFree}
        setIsFree={setIsActivityFree}
        paymentSplitData={paymentSplitData}
        setPaymentSplitData={setPaymentSplitData}
        tripCurrency={tripCurrency}
        formatCurrency={formatCurrency}
        onSave={handleSaveActivityPayments}
        onCancel={() => { 
          setShowActivityPaymentsDialog(false); 
          setViewingActivity(null); 
          setPaymentSplitData([]); 
          setIsActivityFree(false);
          setHasExistingQuotas(false);
          setIsRefreshingPayment(false);
        }}
        onMarkAsFree={handleShowMarkAsFreeAlert}
        hasExistingPayments={existingPayments.length > 0}
        hasExistingQuotas={hasExistingQuotas}
        isRefreshingPayment={isRefreshingPayment}
        setIsRefreshingPayment={setIsRefreshingPayment}
      />

      {/* Mark as Free Alert */}
      <MarkAsFreeAlert
        open={showMarkAsFreeAlert}
        onOpenChange={setShowMarkAsFreeAlert}
        existingPayments={existingPayments}
        formatCurrency={formatCurrency}
        onCancel={handleCancelMarkAsFree}
        onConfirm={handleConfirmMarkAsFree}
      />

      {/* Nearby Search Dialog */}
      <NearbySearchDialog
        open={showNearbyDialog}
        onOpenChange={setShowNearbyDialog}
        destinationLocation={destinationLocation}
        lastActivityLocation={lastActivityLocation}
        nearbyReferenceType={nearbyReferenceType}
        setNearbyReferenceType={setNearbyReferenceType}
        nearbyCustomPlace={nearbyCustomPlace}
        setNearbyCustomPlace={setNearbyCustomPlace}
        nearbyCategory={nearbyCategory}
        setNearbyCategory={setNearbyCategory}
        nearbyRadius={nearbyRadius}
        setNearbyRadius={setNearbyRadius}
        nearbyResults={nearbyResults}
        nearbyLoading={nearbyLoading}
        onSearch={handleSearchNearby}
        onAddPlace={addNearbyAsActivity}
      />
      {/* PDF Options Dialog (Mobile) */}
      <Dialog open={showPdfOptionsDialog} onOpenChange={setShowPdfOptionsDialog}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle className="text-center">Exportar PDF</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 pt-2">
            <Button
              onClick={handleSharePdf}
              className="w-full h-14 bg-blue-600 hover:bg-blue-700 flex items-center justify-center gap-3"
            >
              <Share2 className="w-5 h-5" />
              <span className="text-base">Compartilhar</span>
            </Button>
            <Button
              onClick={handleDownloadPdf}
              variant="outline"
              className="w-full h-14 flex items-center justify-center gap-3"
            >
              <Download className="w-5 h-5" />
              <span className="text-base">Baixar</span>
            </Button>
            <Button
              onClick={() => { setShowPdfOptionsDialog(false); setPendingPdfData(null); }}
              variant="ghost"
              className="w-full text-slate-400"
            >
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
